"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useAnalytics } from "@/lib/analytics";
import { useMyLinkProfileQuery } from "@/modules/creator/services/creatorClient";
import { authService } from "@/lib/authService";
import { isPublicCreatorRoute } from "@/lib/analytics/posthogFeatureFlags";

// Pages where the user is definitely NOT logged in — skip API call
const PUBLIC_ONLY_PATHS = ["/login", "/register", "/"];

/**
 * Fetches creator profile and identifies the user in PostHog + Customer.io.
 * Runs once per session (skips if already identified or on public-only pages).
 */
export function AnalyticsIdentifier() {
  const pathname = usePathname();
  const { identifyCreator } = useAnalytics();
  const identifiedKey = useRef<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const isPublicOnly = PUBLIC_ONLY_PATHS.includes(pathname) || isPublicCreatorRoute(pathname);

  useEffect(() => {
    const syncAuth = () => {
      setIsAuthenticated(authService.isAuthenticated());
    };

    syncAuth();
    window.addEventListener("auth-change", syncAuth);
    return () => window.removeEventListener("auth-change", syncAuth);
  }, []);

  // Only fetch profile when authenticated, not on public-only pages, and not yet identified.
  const { data: creator } = useMyLinkProfileQuery({
    enabled: isAuthenticated && !isPublicOnly,
  });

  useEffect(() => {
    if (isPublicOnly) {
      identifiedKey.current = null;
      return;
    }
    if (!creator?.id) return;

    const primaryPlatform = creator.primaryPlatform;
    const nicheCategories = creator.settings?.nicheCategories || [];
    const primaryAccount = creator.socialAccounts?.find(
      (account) => account.provider === primaryPlatform
    );
    const identityKey = JSON.stringify({
      id: creator.id,
      email: creator.email,
      username: creator.username,
      displayName: creator.displayName,
      primaryPlatform,
      primaryAccountHandle: primaryAccount?.handle,
      primaryAccountProfileUrl: primaryAccount?.profileUrl,
      totalFollowers: creator.totalFollowers,
      nicheCategories,
    });

    if (identifiedKey.current === identityKey) return;

    identifyCreator({
      id: creator.id,
      username: creator.username,
      email: creator.email,
      name: creator.displayName,
      displayName: creator.displayName,
      nicheCategories,
      primaryPlatform,
      primarySocialAccount: primaryPlatform,
      primarySocialUrl: primaryAccount?.profileUrl,
      totalFollowers: creator.totalFollowers,
      enrichmentStatus: undefined,
    });

    identifiedKey.current = identityKey;
  }, [creator, identifyCreator, isPublicOnly]);

  return null;
}
